/**
 * Application route constants and helper functions
 */

// ===== Route Paths =====
export const ROUTES = {
    // Public routes
    EVENTS: "/events",
    FAQ: "/FAQ",
    PRIVACY_POLICY: "/privacy-policy",
    PARTNERS: "/partners",
    TERMS: "/terms",
    ABOUT: "/about",

    // Sport routes
    FORMULA_1: "/motorsport/formula-1",
    TENNIS_TOURNAMENTS: "/tennis/tournaments",

    GENERAL_TERMS_LINK: "https://cdn.xs2event.com/generalterms/general-terms-XS2Event_v1.4.pdf",
} as const;


// ===== Dynamic Route Builders =====
/**
 * Build events page URL with team filter
 * @param team - Team slug or name
 * @param additionalParams - Optional additional query parameters
 * @returns URL string
 */
export const eventsWithTeam = (team: string, additionalParams?: Record<string, string>): string => {
    const additional = additionalParams && Object.keys(additionalParams).length > 0
        ? "&" + new URLSearchParams(additionalParams).toString()
        : "";
    return `${ROUTES.EVENTS}?team=${team}${additional}`;
};

/**
 * Build events page URL with tournament filter
 * @param tournament - Tournament slug
 * @param additionalParams - Optional additional query parameters
 * @returns URL string
 */
export const eventsWithTournament = (tournament: string, additionalParams?: Record<string, string>): string => {
    const additional = additionalParams && Object.keys(additionalParams).length > 0
        ? "&" + new URLSearchParams(additionalParams).toString()
        : "";
    return `${ROUTES.EVENTS}?tournament=${tournament}${additional}`;
};

/**
 * Build events page URL with sport type filter
 * @param sportType - Sport type (e.g., 'football', 'formula1', 'tennis')
 * @param additionalParams - Optional additional query parameters
 * @returns URL string
 */
export const eventsWithSportType = (sportType: string | string[], additionalParams?: Record<string, string>): string => {
    const sportTypeValue = Array.isArray(sportType) ? sportType.join(",") : sportType;
    const additional = additionalParams && Object.keys(additionalParams).length > 0
        ? "&" + new URLSearchParams(additionalParams).toString()
        : "";
    return `${ROUTES.EVENTS}?sport_type=${sportTypeValue}${additional}`;
};

/**
 * Build events page URL with venue filter
 * @param venue - Venue name
 * @param additionalParams - Optional additional query parameters
 * @returns URL string
 */
export const eventsWithVenue = (venue: string, additionalParams?: Record<string, string>): string => {
    const additional = additionalParams && Object.keys(additionalParams).length > 0
        ? "&" + new URLSearchParams(additionalParams).toString()
        : "";
    return `${ROUTES.EVENTS}?venue=${venue}${additional}`;
};

/**
 * Build events page URL with city filter
 * @param city - City name
 * @param additionalParams - Optional additional query parameters
 * @returns URL string
 */
export const eventsWithCity = (city: string, additionalParams?: Record<string, string>): string => {
    const additional = additionalParams && Object.keys(additionalParams).length > 0
        ? "&" + new URLSearchParams(additionalParams).toString()
        : "";
    return `${ROUTES.EVENTS}?city=${city}${additional}`;
};

/**
 * Build events page URL with country filter
 * @param isoCountry - ISO country code (e.g., 'gbr', 'esp')
 * @param additionalParams - Optional additional query parameters
 * @returns URL string
 */
export const eventsWithCountry = (isoCountry: string, additionalParams?: Record<string, string>): string => {
    const additional = additionalParams && Object.keys(additionalParams).length > 0
        ? "&" + new URLSearchParams(additionalParams).toString()
        : "";
    return `${ROUTES.EVENTS}?iso_country=${isoCountry}${additional}`;
};

